// SocialHiveEvents.js (güncellenmiş hali)
import React, { useEffect, useState } from 'react';
import './SocialHiveEvents.css';
import SHEventCard from '../../components/SHEventCard/SHEventCard';
import UserSearchInput from '../../../ProjectsWindow/components/ProjectAuthorization/components/UserSearchInput';
import { useLanguage } from '../../../../LanguageProvider';
import { HiPlus } from 'react-icons/hi';
import { FiShare2 } from 'react-icons/fi';
import { TbCalendarMonth, TbArrowLeft } from 'react-icons/tb';
import AddEventModal from '../../components/AddEventModal/AddEventModal';
import SHEventInfoPage from '../SHEventInfoPage/SHEventInfoPage';
import Services from '../../../../services/Services';
import { useToaster } from '../../../../ToasterProvider';

// Custom Events Toggle Component
function EventsToggle({ showPastEvents, setShowPastEvents, getTranslation }) {
  return (
    <div className="events-toggle">
      <button
        className={`events-toggle-segment ${!showPastEvents ? 'active' : ''}`}
        onClick={() => setShowPastEvents(false)}
      >
        {getTranslation("futureEvents")}
      </button>
      <button
        className={`events-toggle-segment ${showPastEvents ? 'active' : ''}`}
        onClick={() => setShowPastEvents(true)}
      >
        {getTranslation("pastEvents")}
      </button>
    </div>
  );
}

// Enhanced Empty State Component
function EmptyEventState({ isAdmin, getTranslation, onCreateEvent, titleOverride, descriptionOverride }) {
  return (
    <div className="empty-events-state">
      <div className="empty-events-icon">
        <TbCalendarMonth size={48} />
      </div>

      <div className="empty-events-content">
        <h3 className="empty-events-title">
          {titleOverride || (isAdmin ? getTranslation("createAnEvent") : getTranslation("noUpcomingEvents"))}
        </h3>

        <p className="empty-events-description">
          {descriptionOverride || (
            isAdmin
              ? getTranslation("adminEmptyStateDescription")
              : getTranslation("attendeeEmptyStateDescription")
          )}
        </p>

        {isAdmin && (
          <button
            className="empty-events-action-button"
            onClick={onCreateEvent}
          >
            {getTranslation("createEvent")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SocialHiveEvents({
  selectedEvent,
  setSelectedEvent,
  activeChannel,
  handleChannelChange,
  // Events.jsx’ten gelince true gönderiyoruz
  showBackToEvents = false,
  // Events.jsx’ten ana sayfaya dönmek için callback
  onBackFromEvents,
}) {
  const token = localStorage.getItem("token")
  const { toastError, toastSuccess } = useToaster();
  const { getTranslation, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState();
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [memberCount, setMemberCount] = useState(null);

  const monthKeys = [
    'january', 'february', 'march', 'april',
    'may', 'june', 'july', 'august',
    'september', 'october', 'november', 'december'
  ];

  const handleEventSelect = (event) => setSelectedEvent(event);

  function formatToMonthDashDay(timestamp) {
    const date = new Date(timestamp * 1000);
    const monthKey = monthKeys[date.getMonth()];
    const monthName = getTranslation(monthKey);           // örn. "October" veya "Ekim"
    const shortMonth = monthName.slice(0, 3);             // "Oct" veya "Eki"
    const day = String(date.getDate()).padStart(2, '0');  // "03"
    return `${shortMonth} - ${day}`;
  }

  function formatToDayShortMonthYear(timestamp) {
    const date = new Date(timestamp * 1000);
    const day = date.getDate();                           // 3
    const monthKey = monthKeys[date.getMonth()];
    const monthName = getTranslation(monthKey);           // "October" veya "Ekim"
    const shortMonth = monthName.slice(0, 3);             // "Oct" veya "Eki"
    const year = date.getFullYear();                      // 2025
    return `${day} ${shortMonth} ${year}`;
  }

  // Language-aware date formatting: 10 September 2025, Saturday
  function formatToLongDate(timestamp) {
    try {
      const d = new Date(timestamp * 1000);

      // Map language codes to locale codes
      const localeMap = {
        'en': 'en-US',
        'tr': 'tr-TR',
        'es': 'es-ES',
        'de': 'de-DE',
        'it': 'it-IT',
        'fr': 'fr-FR'
      };

      const locale = localeMap[language] || 'en-US';

      const main = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).format(d);
      const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(d);
      const capWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${main}, ${capWeekday}`;
    } catch (e) {
      return formatToDayShortMonthYear(timestamp);
    }
  }

  const getEvents = () => {
    setIsLoading(true)
    let channelID = selectedEvent?.channelID || activeChannel.channelID
    Services.SocialHiveGetEvents(token, channelID)
      .then((res) => {
        setIsLoading(false)
        if (res.data.status) {
          setEvents(res.data.result)
          setFilteredEvents(res.data.result)
        } else {
          setEvents([])
          setFilteredEvents([])
        }
      })
      .catch((err) => {
        console.error(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  }

  useEffect(() => {
    getEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel.channelID]);

  // Try to populate member count. Prefer data from channel object if present; fall back to fetching users and counting.
  useEffect(() => {
    const initial = Number(activeChannel?.channelUserCount);
    if (!Number.isNaN(initial) && initial > 0) {
      setMemberCount(initial);
      return;
    }
    if (!activeChannel?.channelID) return;
    Services.SocialHiveGetChannelUsers(token, activeChannel.channelID)
      .then((res) => {
        if (res?.data?.status && Array.isArray(res?.data?.result)) {
          setMemberCount(res.data.result.length);
        }
      })
      .catch(() => { });
  }, [activeChannel?.channelID, activeChannel?.channelUserCount, token]);

  // Filtreleme + sıralama (yeniden → eskiye)
  const filterEvents = () => {
    const filtered = events.filter((evt) => {
      const title = (evt.title || "").toLowerCase();
      const matchesSearch = searchQuery ? title.includes(searchQuery.toLowerCase()) : true;

      return matchesSearch;
    })
      // yeniden → eskiye
      .sort((a, b) => Number(b.eventStartDateTime) - Number(a.eventStartDateTime));

    setFilteredEvents(filtered);
  };

  useEffect(() => {
    filterEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, events]);

  const handleSearch = (value) => setSearchQuery(value);

  const handleCopyShareLink = () => {
    const url = `https://lodos.sh/channel/${activeChannel.channelURI}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toastSuccess(getTranslation("copiedToClipboard"));
      })
      .catch(err => {
        console.error('Copy failed:', err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  // Geçmiş / Yaklaşan ayırma
  const nowSec = Math.floor(Date.now() / 1000);
  const upcomingEvents = filteredEvents.filter(
    e => Number(e.eventStartDateTime) >= nowSec
  );
  const ongoingEvents = filteredEvents.filter(
    e => Number(e.eventStartDateTime) < nowSec && Number(e.eventEndDateTime) >= nowSec
  );
  const pastEvents = filteredEvents.filter(
    e => Number(e.eventEndDateTime) < nowSec
  );

  return (
    <div className='social-hive-pages-main' style={{ paddingTop: 0, alignItems: 'center' }}>
      {(selectedEvent && events.length > 0) ? (
        <SHEventInfoPage
          event={selectedEvent}
          channelTitle={events?.find(x => x.eventID === selectedEvent?.eventID)?.channelTitle}
          channelLogo={events?.find(x => x.eventID === selectedEvent?.eventID)?.logo}
          setSelectedEvent={setSelectedEvent}
          isAdmin={activeChannel.isAdmin}
          getEvents={getEvents}
          handleChannelChange={handleChannelChange}
        />
      ) : (
        <>
          {/* BACK BUTONU: sadece showBackToEvents true ise */}
          {showBackToEvents && (
            <div className="events-back-row">
              <button
                type="button"
                className="events-back-button"
                onClick={() => {
                  if (onBackFromEvents) {
                    onBackFromEvents();
                  } else if (typeof handleChannelChange === "function") {
                    handleChannelChange(null);
                  }
                }}
              >
                <TbArrowLeft size={16} />
                {getTranslation("back") || "Etkinliklere Dön"}
              </button>
            </div>
          )}

          {/* Events Toggle Section */}
          <div className="events-toggle-section">
            <EventsToggle
              showPastEvents={showPastEvents}
              setShowPastEvents={setShowPastEvents}
              getTranslation={getTranslation}
            />

            {/* Right side actions */}
            <div className="toggle-actions-container">
              {/* Past events: compact search to the LEFT of share */}
              {showPastEvents && (
                <div className="compact-search">
                  <UserSearchInput
                    style={{ height: '15px', minWidth: '180px' }}
                    placeholder={getTranslation("search")}
                    onSearch={handleSearch}
                    value={searchQuery}
                  />
                </div>
              )}

              {/* Add Event button: only on Upcoming tab (LEFT of Share) */}
              {activeChannel.isAdmin === "1" && !showPastEvents && (
                <button
                  className="kronos-add-manual-button"
                  onClick={() => setIsAddModalOpen(true)}
                  title={getTranslation("createEvent")}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  <HiPlus size={16} />
                  {getTranslation("createEvent")}
                </button>
              )}

              <button
                className="kronos-add-manual-button"
                title={getTranslation("shareCommunity")}
                onClick={handleCopyShareLink}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <FiShare2 size={14} />
                {getTranslation("shareCommunity")}
              </button>
            </div>
          </div>

          <div className="social-hive-events-layout">
            <div className="events-main">
              {/* Show Future/Upcoming Events */}
              {!showPastEvents && (
                <>
                  {/* Upcoming Events */}
                  {upcomingEvents.map((evt) => (
                    <SHEventCard
                      isOnline={evt.isOnline}
                      website={evt.website}
                      key={evt.eventID || evt.id}
                      imageSrc={evt.cover}
                      title={evt.title}
                      dateBadge={formatToMonthDashDay(evt.eventStartDateTime)}
                      location={evt.locationAddress}
                      date={formatToLongDate(evt.eventStartDateTime)}
                      onClick={() => handleEventSelect(evt)}
                      isFree={evt.isFree}
                      isAdmin={activeChannel.isAdmin}
                      event={evt}
                      getEvents={getEvents}
                    />
                  ))}

                  {/* Ongoing Events */}
                  {ongoingEvents.map((evt) => (
                    <SHEventCard
                      isOnline={evt.isOnline}
                      website={evt.website}
                      key={`ongoing-${evt.eventID || evt.id}`}
                      imageSrc={evt.cover}
                      title={evt.title}
                      dateBadge={formatToMonthDashDay(evt.eventStartDateTime)}
                      location={evt.locationAddress}
                      date={formatToLongDate(evt.eventStartDateTime)}
                      onClick={() => handleEventSelect(evt)}
                      isFree={evt.isFree}
                      isAdmin={activeChannel.isAdmin}
                      event={evt}
                      getEvents={getEvents}
                    />
                  ))}

                  {/* Enhanced Empty State */}
                  {upcomingEvents.length === 0 && ongoingEvents.length === 0 && !isLoading && (
                    <EmptyEventState
                      isAdmin={activeChannel.isAdmin === "1"}
                      getTranslation={getTranslation}
                      onCreateEvent={() => setIsAddModalOpen(true)}
                      titleOverride={searchQuery ? getTranslation('noSearchResultsTitle') : undefined}
                      descriptionOverride={searchQuery ? getTranslation('noSearchResultsDescription') : undefined}
                    />
                  )}
                </>
              )}

              {/* Show Past Events */}
              {showPastEvents && (
                <>
                  {pastEvents.map((evt) => (
                    <SHEventCard
                      isOnline={evt.isOnline}
                      website={evt.website}
                      key={`past-${evt.eventID || evt.id}`}
                      imageSrc={evt.cover}
                      title={evt.title}
                      dateBadge={formatToMonthDashDay(evt.eventStartDateTime)}
                      location={evt.locationAddress}
                      date={formatToLongDate(evt.eventStartDateTime)}
                      onClick={() => handleEventSelect(evt)}
                      isFree={evt.isFree}
                      isAdmin={activeChannel.isAdmin}
                      event={evt}
                      getEvents={getEvents}
                    />
                  ))}

                  {/* Enhanced Empty State for Past Events */}
                  {pastEvents.length === 0 && (
                    <EmptyEventState
                      isAdmin={activeChannel.isAdmin === "1"}
                      getTranslation={getTranslation}
                      onCreateEvent={() => setIsAddModalOpen(true)}
                      titleOverride={searchQuery ? getTranslation('noSearchResultsTitle') : getTranslation('pastEmptyTitle')}
                      descriptionOverride={searchQuery ? getTranslation('noSearchResultsDescription') : getTranslation('pastEmptyDescription')}
                    />
                  )}
                </>
              )}
            </div>

            {/* Right side community panel (shown when no event is selected) */}
            <aside className="events-right-panel">
              <div className="community-card">
                <div className="community-hero">
                  {activeChannel?.topBanner ? (
                    <img src={activeChannel.topBanner} alt={activeChannel?.title || 'community'} className="community-banner" />
                  ) : (
                    <div className="community-banner" />
                  )}
                  <div className="community-avatar-container">
                    {activeChannel?.logo ? (
                      <img src={activeChannel.logo} alt={activeChannel?.title || 'community'} className="community-avatar" />
                    ) : (
                      <div className="community-avatar placeholder" />
                    )}
                  </div>
                </div>

                <div className="community-title">{activeChannel?.title}</div>

                <div className="community-stats">
                  <div className="stat">
                    <span className="stat-value">{memberCount ?? '-'}</span>
                    <span className="stat-label">{getTranslation('participant')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{events?.length ?? 0}</span>
                    <span className="stat-label">{getTranslation('events')}</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <AddEventModal
            getEvents={getEvents}
            channelID={activeChannel.channelID}
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
          />
        </>
      )}
    </div>
  );
}